import React, { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { AnimatedViews, useResponsive, VStack, type AddressType } from '@sg/garnish';
import { FlattenedOrderStatuses, OrderType } from '@sg/graphql-schema';

import { OrderStatus } from '@order/features-order-status';
import {
  Receipt,
  useReceiptMachine,
  type ReceiptMachineImplementations,
} from '@order/features-receipt';
import { useContactUsHandler } from '@order/shared-hooks';
import { useTrackEffect } from '@order/Telemetry';
import { parsePayments } from '@order/utils';

import { AppFooter, SignedOutView } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';

export const ReceiptScreen = () => {
  const isLoggingIn = useIsLoggingIn();

  if (isLoggingIn) return <OrderStatus.Loading />;

  return <ReceiptScreenContent />;
};

// ─── Content ─────────────────────────────────────────────────────────────────

const ReceiptScreenContent = () => {
  const { formatMessage } = useIntl();

  const { receiptId } = useLocalSearchParams<{ receiptId: string }>();

  const isLoggedIn = useIsLoggedIn();

  const {
    isFetchingReceipt,
    isViewing,
    isExpiredError,
    isTransactionNotFoundError,
    isReceiptAlreadyClaimedError,
    isUnknownError,
    order,
    isViewingClaimedReceipt,
  } = useReceiptMachine(receiptId, RECEIPT_MACHINE_IMPLEMENTATIONS);

  // ─── Loading ───────────────────────────────────────────────────────────────

  if (isFetchingReceipt) return <OrderStatus.Loading />;

  // ─── Ready ─────────────────────────────────────────────────────────────────

  if (isViewing && order) {
    return (
      <AnimatedViews.Reveal style={styles.container}>
        <ReceiptScreenReady
          isLoggedIn={isLoggedIn}
          receiptId={receiptId}
          order={order}
          isClaimed={isViewingClaimedReceipt}
        />
      </AnimatedViews.Reveal>
    );
  }

  // ─── Error States ────────────────────────────────────────────────────────

  if (isReceiptAlreadyClaimedError) {
    return isLoggedIn ? <ClaimedReceiptView /> : <SignedOutView />;
  }

  if (isTransactionNotFoundError) return <NotFoundReceiptView />;

  if (isExpiredError) return <ExpiredReceiptView />;

  if (isUnknownError) return <Receipt.UnavailableView title={formatMessage(messages.errorTitle)} />;

  return <Receipt.UnavailableView title={formatMessage(messages.errorTitle)} />;
};

const ReceiptScreenReady = (props: ReceiptScreenReadyProps) => {
  const { order, isClaimed, receiptId, isLoggedIn } = props;

  const { currentBreakpoint } = useResponsive();

  const { handleContactUs } = useContactUsHandler('receipt');

  // ─── Parse data ───────────────────────────────────────────────────────────────────

  const orderId = order.id;
  const orderStatus = FlattenedOrderStatuses.Completed;
  const orderTotal = order.total ?? undefined;

  const isRestaurantOutpost = false;
  const orderType = getOrderType(order.orderType, isRestaurantOutpost);

  const {
    id: restaurantId,
    name: locationName,
    address: locationAddress,
    city: locationCity,
    state: locationState,
    zipCode: locationZipCode,
    latitude: locationLatitude,
    longitude: locationLongitude,
  } = order.restaurant;

  const payments = useMemo(() => parsePayments(order.payments), [order.payments]);

  // ─────────────────────────────────────────────────────────────────────────────

  useTrackEffect('receipt.screen_viewed', { isLoggedIn, isClaimed });

  // ─────────────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <OrderStatus.Layout.ContainerWrapper>
        <OrderStatus.Layout.Container>
          <OrderStatus.Layout.Info>
            <OrderStatus.Info
              orderId={orderId}
              orderType={orderType}
              orderStatus={orderStatus}
              orderTotal={orderTotal}
              wantedTime={order.wantedTime}
              vendorId={order.vendorId}
              locationName={locationName}
              locationAddress={locationAddress}
              canRateOrder={order.isPendingFeedback}
              isTrackable={false}
              canReorder={false}
              isPast
            />
          </OrderStatus.Layout.Info>

          <OrderStatus.Layout.Map>
            <OrderStatus.Map
              orderType={orderType}
              orderStatus={orderStatus}
              locationName={locationName}
              locationLatitude={locationLatitude}
              locationLongitude={locationLongitude}
            />
          </OrderStatus.Layout.Map>

          <OrderStatus.Layout.Content>
            <VStack gap={0} divider={<OrderStatus.Divider />}>
              <Receipt.ClaimBanner
                isLoggedIn={isLoggedIn}
                isClaimed={isClaimed}
                receiptId={receiptId}
              />

              <OrderStatus.Address
                orderType={orderType}
                locationName={locationName}
                locationAddress={locationAddress}
                locationCity={locationCity}
                locationState={locationState}
                locationZipCode={locationZipCode}
              />

              <View>
                <Receipt.LineItems lineItems={order.lineItems} />

                <OrderStatus.Ledger ledger={order.ledger} />
              </View>

              <OrderStatus.Payments payments={payments} />
            </VStack>

            <OrderStatus.ContactUs
              orderId={orderId}
              canCancel={false}
              restaurantId={restaurantId}
              onContactUs={handleContactUs}
              shouldHideCancelOption
            />
          </OrderStatus.Layout.Content>
        </OrderStatus.Layout.Container>
      </OrderStatus.Layout.ContainerWrapper>
    );
  }

  return (
    <OrderStatus.Layout.ContainerWrapper>
      <OrderStatus.Layout.Container>
        <OrderStatus.Layout.Map>
          <OrderStatus.Map
            orderType={orderType}
            orderStatus={orderStatus}
            locationName={locationName}
            locationLatitude={locationLatitude}
            locationLongitude={locationLongitude}
          />
        </OrderStatus.Layout.Map>

        <OrderStatus.Layout.SidePanel>
          <OrderStatus.Layout.Info>
            <OrderStatus.Info
              orderId={orderId}
              orderType={orderType}
              orderStatus={orderStatus}
              orderTotal={orderTotal}
              wantedTime={order.wantedTime}
              vendorId={order.vendorId}
              locationName={locationName}
              locationAddress={locationAddress}
              canRateOrder={order.isPendingFeedback}
              isTrackable={false}
              canReorder={false}
              isPast
            />
          </OrderStatus.Layout.Info>

          <OrderStatus.Layout.Content>
            <VStack gap={0} divider={<OrderStatus.Divider />}>
              <Receipt.ClaimBanner
                isLoggedIn={isLoggedIn}
                isClaimed={isClaimed}
                receiptId={receiptId}
              />

              <OrderStatus.Address
                orderType={orderType}
                locationName={locationName}
                locationAddress={locationAddress}
                locationCity={locationCity}
                locationState={locationState}
                locationZipCode={locationZipCode}
              />

              <View>
                <Receipt.LineItems lineItems={order.lineItems} />

                <OrderStatus.Ledger ledger={order.ledger} />
              </View>

              <OrderStatus.Payments payments={payments} />
            </VStack>

            <OrderStatus.ContactUs
              orderId={orderId}
              canCancel={false}
              restaurantId={restaurantId}
              onContactUs={handleContactUs}
              shouldHideCancelOption
            />
          </OrderStatus.Layout.Content>
        </OrderStatus.Layout.SidePanel>
      </OrderStatus.Layout.Container>

      <AppFooter />
    </OrderStatus.Layout.ContainerWrapper>
  );
};

// ─── Unavailable Receipt Screen States ───────────────────────────────────────────────────────────────────

const NotFoundReceiptView = () => {
  const { formatMessage } = useIntl();

  return (
    <Receipt.UnavailableView
      title={formatMessage(messages.notFoundTitle)}
      description={formatMessage(messages.notFoundDescription)}
    />
  );
};

const ExpiredReceiptView = () => {
  const { formatMessage } = useIntl();

  return (
    <Receipt.UnavailableView
      title={formatMessage(messages.expiredTitle)}
      description={formatMessage(messages.expiredDescription)}
      shouldDisplayContactUs
    />
  );
};

const ClaimedReceiptView = () => {
  const { formatMessage } = useIntl();

  return (
    <Receipt.UnavailableView
      title={formatMessage(messages.claimedTitle)}
      description={formatMessage(messages.claimedDescription)}
      shouldDisplayContactUs
    />
  );
};

// ─── Pure Functions ───────────────────────────────────────────────────────────────────

const getOrderType = (orderType?: OrderType, isRestaurantOutpost?: boolean): AddressType => {
  if (isRestaurantOutpost) return 'outpost';
  if (orderType === OrderType.Delivery) return 'delivery';
  if (orderType === OrderType.InStore) return 'in-store';

  return 'pickup';
};

// ─── Constants ───────────────────────────────────────────────────────────────────

const RECEIPT_MACHINE_IMPLEMENTATIONS = {
  guards: { checkIfShouldAutoClaim: () => false },
} satisfies ReceiptMachineImplementations;

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  claimedTitle: {
    defaultMessage: 'This receipt has already been claimed.',
    description: 'Receipt | Already Claimed View | Title',
  },
  claimedDescription: {
    defaultMessage: "Doesn't look right?",
    description: 'Receipt | Already Claimed View | Description',
  },
  notFoundTitle: {
    defaultMessage: "We couldn't find that receipt",
    description: 'Receipt | Not Found View | Title',
  },
  notFoundDescription: {
    defaultMessage: 'Need help finding your receipt?',
    description: 'Receipt | Expired View | Description',
  },
  expiredTitle: {
    defaultMessage: 'This receipt is older than 30 days and has expired.',
    description: 'Receipt | Expired View | Title',
  },
  expiredDescription: {
    defaultMessage: 'Do you still need this receipt?',
    description: 'Receipt | Expired View | Description',
  },
  errorTitle: {
    defaultMessage: 'We encountered an error while fetching your receipt.',
    description: 'Receipt | Unknown Error View | Title',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ReceiptOrder = NonNullable<ReturnType<typeof useReceiptMachine>['order']>;

type ReceiptScreenReadyProps = {
  order: ReceiptOrder;
  isClaimed: boolean;
  receiptId: string;
  isLoggedIn: boolean;
};
